/**
 * ShareMenu — universal share dropdown with all major channels.
 *
 * Usage:
 *   <ShareMenu
 *     url="/hire/rfs/abc123"        (relative OR absolute; backend
 *                                    always rewrites to prod origin)
 *     title="Airport pickup COK to Pala"
 *     text="Need help finding an airport driver? Check this out."
 *     entityType="rfs"              (for intelligence attribution)
 *     entityId="abc123"
 *   />
 *
 * Channels: Copy · Native (mobile share sheet) · WhatsApp · Email ·
 * SMS · Facebook · X (Twitter) · LinkedIn · Telegram · Reddit ·
 * Pinterest · QR code (when SpLink returns one)
 *
 * The button first asks the backend to mint a branded short URL
 * (falls back to the raw long URL if SpLink isn't configured yet).
 * Every channel click fires a `share_click` intelligence event so we
 * can measure sharing behaviour and feed it into Impact Score.
 */
import { useEffect, useState } from "react";
import { http, httpPublic } from "@/lib/api";
import { trackIntel } from "@/lib/analytics";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Share2, Copy, Check, MessageCircle, Mail, MessageSquare, Facebook,
  Twitter, Linkedin, Send, QrCode, ExternalLink,
} from "lucide-react";

export default function ShareMenu({
  url,
  title = "",
  text = "",
  entityType = null,
  entityId = null,
  buttonLabel = "Share",
  buttonVariant = "outline",
  buttonSize = "sm",
  className = "",
  testid = "share-menu",
}) {
  const [open, setOpen] = useState(false);
  const [shortUrl, setShortUrl] = useState(url || "");
  const [qrUrl, setQrUrl] = useState(null);
  const [via, setVia] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Mint short URL when the modal opens.
  useEffect(() => {
    if (!open || !url) return;
    let alive = true;
    setLoading(true);
    // Use public http so anonymous users can still share
    (httpPublic || http).post("/sp-share/short-link", {
      long_url: url,
      label: title || undefined,
    }).then(({ data }) => {
      if (!alive) return;
      setShortUrl(data.short_url || url);
      setQrUrl(data.qr_code_url || null);
      setVia(data.via);
    }).catch(() => {
      if (alive) setShortUrl(url);
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [open, url, title]);

  const share = async (channel) => {
    // Fire tracking event (fire-and-forget, don't block navigation)
    try {
      (httpPublic || http).post("/sp-share/track", {
        channel, url: shortUrl,
        entity_type: entityType, entity_id: entityId,
      }).catch(() => {});
    } catch {}
    trackIntel("share_click", { channel, entity_type: entityType, entity_id: entityId });

    const encUrl = encodeURIComponent(shortUrl);
    const encTitle = encodeURIComponent(title);
    const encText = encodeURIComponent(text || title);
    const combined = encodeURIComponent(`${title ? title + " " : ""}${shortUrl}`);

    switch (channel) {
      case "copy": {
        try {
          await navigator.clipboard.writeText(shortUrl);
          setCopied(true);
          toast.success("Link copied");
          setTimeout(() => setCopied(false), 2000);
        } catch {
          toast.error("Couldn't copy — please copy manually");
        }
        return;
      }
      case "native": {
        if (navigator.share) {
          try {
            await navigator.share({ title, text, url: shortUrl });
          } catch { /* user cancelled */ }
        }
        return;
      }
      case "whatsapp":
        window.open(`https://wa.me/?text=${combined}`, "_blank", "noopener");
        return;
      case "email":
        window.location.href = `mailto:?subject=${encTitle}&body=${encText}%0A%0A${encUrl}`;
        return;
      case "sms":
        // iOS + Android both accept this format
        window.location.href = `sms:?&body=${combined}`;
        return;
      case "facebook":
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encUrl}`, "_blank", "noopener,width=600,height=600");
        return;
      case "twitter":
        window.open(`https://twitter.com/intent/tweet?url=${encUrl}&text=${encTitle}`, "_blank", "noopener,width=600,height=600");
        return;
      case "linkedin":
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`, "_blank", "noopener,width=600,height=600");
        return;
      case "telegram":
        window.open(`https://t.me/share/url?url=${encUrl}&text=${encTitle}`, "_blank", "noopener");
        return;
      case "reddit":
        window.open(`https://www.reddit.com/submit?url=${encUrl}&title=${encTitle}`, "_blank", "noopener,width=800,height=700");
        return;
      case "pinterest":
        window.open(`https://pinterest.com/pin/create/button/?url=${encUrl}&description=${encTitle}`, "_blank", "noopener");
        return;
      default:
        return;
    }
  };

  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;
  const targets = [
    { key: "copy",      label: copied ? "Copied" : "Copy link", icon: copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />, color: "bg-slate-100 text-slate-700" },
    ...(canNativeShare ? [{ key: "native", label: "More…", icon: <Share2 className="w-5 h-5" />, color: "bg-slate-100 text-slate-700" }] : []),
    { key: "whatsapp",  label: "WhatsApp",  icon: <MessageCircle className="w-5 h-5" />, color: "bg-emerald-500 text-white" },
    { key: "email",     label: "Email",     icon: <Mail className="w-5 h-5" />,          color: "bg-blue-500 text-white" },
    { key: "sms",       label: "SMS",       icon: <MessageSquare className="w-5 h-5" />, color: "bg-green-600 text-white" },
    { key: "facebook",  label: "Facebook",  icon: <Facebook className="w-5 h-5" />,      color: "bg-[#1877F2] text-white" },
    { key: "twitter",   label: "X",         icon: <Twitter className="w-5 h-5" />,       color: "bg-black text-white" },
    { key: "linkedin",  label: "LinkedIn",  icon: <Linkedin className="w-5 h-5" />,      color: "bg-[#0A66C2] text-white" },
    { key: "telegram",  label: "Telegram",  icon: <Send className="w-5 h-5" />,          color: "bg-[#26A5E4] text-white" },
    { key: "reddit",    label: "Reddit",    icon: <ExternalLink className="w-5 h-5" />,  color: "bg-[#FF4500] text-white" },
    { key: "pinterest", label: "Pinterest", icon: <ExternalLink className="w-5 h-5" />,  color: "bg-[#E60023] text-white" },
  ];

  return (
    <>
      <Button
        variant={buttonVariant}
        size={buttonSize}
        onClick={() => setOpen(true)}
        data-testid={testid}
        className={className}
      >
        <Share2 className="w-4 h-4 mr-1.5" />{buttonLabel}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle>Share</DialogTitle>
          </DialogHeader>

          <div className="mb-3 mt-1">
            <div className="text-xs text-slate-500 mb-1">Link</div>
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-3 py-2">
              <div className="flex-1 text-sm font-mono truncate" title={shortUrl} data-testid="share-url-display">
                {loading ? "…" : shortUrl}
              </div>
              <button
                onClick={() => share("copy")}
                className="flex-shrink-0 p-1.5 text-slate-500 hover:text-slate-900"
                data-testid="share-copy-inline"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            {via === "splink" && (
              <div className="text-[10px] text-emerald-700 mt-1">✓ Branded short URL via SpLink</div>
            )}
          </div>

          <div className="grid grid-cols-4 gap-2">
            {targets.map(t => (
              <button
                key={t.key}
                onClick={() => share(t.key)}
                data-testid={`share-${t.key}`}
                className="flex flex-col items-center gap-1.5 p-3 rounded-md hover:bg-slate-100 transition"
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${t.color}`}>{t.icon}</div>
                <span className="text-[11px] text-slate-700 leading-tight text-center">{t.label}</span>
              </button>
            ))}
          </div>

          {qrUrl && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                <QrCode className="w-4 h-4" />QR code
              </div>
              <img src={qrUrl} alt="QR code" className="w-40 h-40 mx-auto" data-testid="share-qr-image" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
